import React from 'react'
import { Modal, PrimaryButton } from 'components'

const PICK_AND_MIX_BUTTON_GROUP_CLASS: string = `flex flex-col items-center gap-y-1`
const PICK_AND_MIX_COLUMN_CLASS: string = `flex flex-col justify-center gap-y-8`

/* Types */
export type PickAndMixOption = {
  text: string
  value: string | string[]
  highlighted?: boolean
}

export type PickAndMixChoiceGroupProps = {
  groupName: string
  choices: PickAndMixOption[]
  handleHighlight?: (choice: PickAndMixOption) => void
}

export type PickAndMixButtonProps = {
  text: string
  onClick: () => void
  color?: string
  fontClass?: string
}

export type PickAndMixProps = {
  open: boolean
  options: PickAndMixOption[]
  defaultSelection?: PickAndMixOption[]
  singleHighlight?: boolean
  onFinish: (selectedFields: PickAndMixOption[]) => void
  onUpdate?: (selectedFields: PickAndMixOption[]) => void
  onHighlight?: (highlightedFields: PickAndMixOption[]) => void
  disableFinish?: (availableChoices: PickAndMixOption[], selectedChoices: PickAndMixOption[]) => boolean
  onClose?: () => void
  onCancel?: () => void
  title?: string
  maxWidth?: 'sm' | 'xs' | 'md' | 'lg' | 'xl'
  cancelButtonText?: string
  finishButtonText?: string
  availableChoicesGroupName?: string
  selectedChoicesGroupName?: string
}

/* Components */
export const PickAndMixButton: React.FC<PickAndMixButtonProps> = (props: PickAndMixButtonProps) => {
  const { text, onClick, color } = props
  const fontClass: string = props.fontClass ?? `text-xl`

  return (
    <button
      className={`rounded p-1 ${fontClass}`}
      style={{
        backgroundColor: '#f5f5f5',
        color: color ?? '#00893f',
        minWidth: '25px',
      }}
      onClick={onClick}
      data-testid={`pick-and-mix-button`}
    >
      {text}
    </button>
  )
}

export const PickAndMixChoiceGroup = (props: PickAndMixChoiceGroupProps) => {
  const { groupName, choices } = props
  const handleHighlight: (choice: PickAndMixOption) => void =
    props.handleHighlight ?? ((choice: PickAndMixOption) => {})

  const choiceListStyle = {
    backgroundColor: `#f8f6f2`,
    width: '225px',
    maxHeight: '400px',
  }

  return (
    <div className={`flex flex-col h-full`} data-testid={`pick-and-mix-choice-group`}>
      <div>{groupName}</div>
      <div className={`overflow-y-auto  overflow-x-auto h-full rounded-sm`} style={choiceListStyle}>
        {choices.map((choice, index) => {
          const choiceStyle = {
            backgroundColor: choice.highlighted === true ? `#ffe38f` : undefined,
          }

          return (
            <div
              className={`py-1 cursor-pointer px-5 select-none`}
              style={choiceStyle}
              onClick={() => {
                handleHighlight(choice)
              }}
              key={index}
            >
              {choice.text}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export const PickAndMix: React.FC<PickAndMixProps> = (props: PickAndMixProps) => {
  const { open, options, onFinish } = props

  const defaultSelection: PickAndMixOption[] = props.defaultSelection ?? []
  const singleHighlight: boolean = props.singleHighlight ?? false
  const onClose: () => void = props.onClose ?? (() => {})
  const onCancel: () => void = props.onCancel ?? (() => {})
  // eslint-disable-next-line
  const onUpdate: (selectedFields: PickAndMixOption[]) => void = props.onUpdate ?? (() => {})
  const onHighlight: (highlightedFields: PickAndMixOption[]) => void = props.onHighlight ?? (() => {})
  const disableFinish: (availableChoices: PickAndMixOption[], selectedChoices: PickAndMixOption[]) => boolean =
    props.disableFinish ?? ((available, selected) => selected.length < 1)
  const title: string = props.title ?? 'Selection'
  const maxWidth: 'sm' | 'xs' | 'md' | 'lg' | 'xl' | undefined = props.maxWidth ?? 'xl'
  const cancelButtonText: string = props.cancelButtonText ?? 'Cancel'
  const finishButtonText: string = props.finishButtonText ?? 'Choose'
  const availableChoicesGroupName: string = props.availableChoicesGroupName ?? 'Available Choices'
  const selectedChoicesGroupName: string = props.selectedChoicesGroupName ?? 'Selected Choices'

  const [availableChoices, setAvailableChoices] = React.useState<PickAndMixOption[]>([...options])
  const [selectedChoices, setSelectedChoices] = React.useState<PickAndMixOption[]>([...defaultSelection])

  const resetSelection = () => {
    setAvailableChoices([...options])
    setSelectedChoices([...defaultSelection])
  }

  const getChoiceText = (choices: PickAndMixOption[]): string[] => choices.map((choice) => choice.text)

  const toggleChoiceHighlight = (
    choiceToHighlight: PickAndMixOption,
    choices: PickAndMixOption[],
    setChoices: React.Dispatch<React.SetStateAction<PickAndMixOption[]>>
  ) => {
    const updatedChoices: PickAndMixOption[] = choices.map((choice) => {
      const modifiedChoice = { ...choice }
      if (modifiedChoice.text === choiceToHighlight.text) {
        // If choice is the one that was clicked
        if (modifiedChoice.highlighted === true) {
          // If choice is highlighted already, un-highlight it
          delete modifiedChoice.highlighted
        } else {
          // If choice is not already highlighted, highlight it
          modifiedChoice.highlighted = true
        }
      } else if (singleHighlight === true) {
        // If only one choice can be highlighted, then
        // un-highlight anything that we didn't click
        delete modifiedChoice.highlighted
      }
      return modifiedChoice
    })
    setChoices(updatedChoices)
    onHighlight(updatedChoices.filter((choice) => choice.highlighted === true))
  }

  const transferHighlighted = (
    sourceChoices: PickAndMixOption[],
    setSourceChoices: React.Dispatch<React.SetStateAction<PickAndMixOption[]>>,
    setDestinationChoices: React.Dispatch<React.SetStateAction<PickAndMixOption[]>>,
    orderDestination?: boolean
  ) => {
    const choicesToMove: PickAndMixOption[] = sourceChoices.filter((choice) => choice.highlighted === true)
    const choicesToMoveText = getChoiceText(choicesToMove)
    const newSourceChoices = sourceChoices.filter((choice) => {
      return !choicesToMoveText.includes(choice.text)
    })

    setSourceChoices(newSourceChoices)
    setDestinationChoices((destinationChoices) => {
      const destinationChoiceText = getChoiceText(destinationChoices)
      let newDestinationChoices: PickAndMixOption[] = []
      if (orderDestination === true) {
        // To preserve the order, just take the options
        // and filter out what we shouldn't include after
        // the transfer
        newDestinationChoices = options.filter(
          (choice) => destinationChoiceText.includes(choice.text) || choicesToMoveText.includes(choice.text)
        )
      } else {
        newDestinationChoices = [...destinationChoices]
        // Push choices to move to the new array
        for (const choice of choicesToMove) {
          if (destinationChoiceText.includes(choice.text)) {
            // Don't push values if they already exist for some reason
            continue
          }
          newDestinationChoices.push(choice)
        }
      }
      // Un-highlight everything
      return newDestinationChoices.map((choice) => {
        const updatedChoice = { ...choice }
        delete updatedChoice.highlighted
        return updatedChoice
      })
    })
  }

  const transferAll = (orderDestination?: boolean) => {
    if (orderDestination) {
      setAvailableChoices([...options])
      setSelectedChoices([])
    } else {
      setAvailableChoices([])
      setSelectedChoices([...options])
    }
  }

  const moveHighlighted = (
    setChoices: React.Dispatch<React.SetStateAction<PickAndMixOption[]>>,
    direction: 'up' | 'down'
  ) =>
    setChoices((choices) => {
      const updatedChoices = [...choices]
      let tempList: PickAndMixOption[] = []

      if (direction === 'down') {
        for (let i = 0; i < updatedChoices.length; i++) {
          if (updatedChoices[i].highlighted === true) {
            tempList.push(updatedChoices[i])
          } else if (tempList.length > 0) {
            updatedChoices[i - tempList.length] = updatedChoices[i]
            for (let j = 0; j < tempList.length; j++) {
              updatedChoices[i - tempList.length + j + 1] = tempList[j]
            }
            tempList = []
          }
        }
      }
      if (direction === 'up') {
        for (let i = updatedChoices.length - 1; i >= 0; i--) {
          if (updatedChoices[i].highlighted === true) {
            tempList.unshift(updatedChoices[i])
          } else if (tempList.length > 0) {
            updatedChoices[i + tempList.length] = updatedChoices[i]
            for (let j = 0; j < tempList.length; j++) {
              updatedChoices[i + j] = tempList[j]
            }
            tempList = []
          }
        }
      }

      return updatedChoices
    })

  React.useEffect(() => {
    onUpdate(selectedChoices)
  }, [selectedChoices, onUpdate])

  return (
    <Modal
      title={title}
      maxWidth={maxWidth}
      open={open}
      onClose={() => {
        resetSelection()
        onClose()
      }}
      testId={`pick-and-mix`}
    >
      <div className={`flex flex-col items-center w-full`}>
        <div className={`flex flex-row gap-x-5 w-full overflow-x-auto pb-5`}>
          <div className={PICK_AND_MIX_COLUMN_CLASS}>
            <PickAndMixChoiceGroup
              groupName={availableChoicesGroupName}
              choices={availableChoices}
              handleHighlight={(choice) => toggleChoiceHighlight(choice, availableChoices, setAvailableChoices)}
            />
          </div>
          <div className={PICK_AND_MIX_COLUMN_CLASS}>
            <div className={PICK_AND_MIX_BUTTON_GROUP_CLASS}>
              <PickAndMixButton text={'>>'} onClick={() => transferAll()} />
              <PickAndMixButton
                text={'>'}
                onClick={() => transferHighlighted(availableChoices, setAvailableChoices, setSelectedChoices)}
              />
            </div>
            <div className={PICK_AND_MIX_BUTTON_GROUP_CLASS}>
              <PickAndMixButton
                text={'<'}
                onClick={() => transferHighlighted(selectedChoices, setSelectedChoices, setAvailableChoices, true)}
              />
              <PickAndMixButton text={'<<'} onClick={() => transferAll(true)} />
            </div>
          </div>
          <div className={PICK_AND_MIX_COLUMN_CLASS}>
            <PickAndMixChoiceGroup
              groupName={selectedChoicesGroupName}
              choices={selectedChoices}
              handleHighlight={(choice) => toggleChoiceHighlight(choice, selectedChoices, setSelectedChoices)}
            />
          </div>
          <div className={PICK_AND_MIX_COLUMN_CLASS}>
            <div className={PICK_AND_MIX_BUTTON_GROUP_CLASS}>
              <PickAndMixButton
                text={'Move Up'}
                onClick={() => moveHighlighted(setSelectedChoices, 'up')}
                fontClass={`text-base`}
                color={`black`}
              />
              <PickAndMixButton
                text={'Move Down'}
                onClick={() => moveHighlighted(setSelectedChoices, 'down')}
                fontClass={`text-base`}
                color={`black`}
              />
            </div>
          </div>
        </div>

        <div className={`flex  flex-wrap justify-center mt-6 mb-4 pr-5 gap-7 w-full`}>
          <PrimaryButton
            outlined
            buttonText={cancelButtonText}
            onClick={() => {
              resetSelection()
              onCancel()
            }}
          />
          <PrimaryButton
            buttonText={finishButtonText}
            disabled={disableFinish(availableChoices, selectedChoices)}
            onClick={() => {
              onFinish(selectedChoices)
              resetSelection()
            }}
          />
        </div>
      </div>
    </Modal>
  )
}
